import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import {
  PDF_COLORS,
  PDF_COMPANY,
  formatGeneratedAt,
  formatRp,
} from "@/lib/pdf";
import logo from "@/logo.png";

import { PAYMENT_METHOD_LABELS } from "../receivable-labels";
import { formatInvoiceDate, formatLongDate } from "./format";

import type { Receivable } from "@artisancode/api-types";

const COMPANY_NAME = PDF_COMPANY.name;
const COMPANY_ADDRESS = PDF_COMPANY.address;

const ORANGE = PDF_COLORS.orange;
const ORANGE_LIGHT = "#FBEAE0";
const DARK = PDF_COLORS.dark;
const GRAY = PDF_COLORS.gray;

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 9, fontFamily: "Helvetica", color: DARK },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: { flexShrink: 1 },
  docTitle: { fontFamily: "Helvetica-Bold", fontSize: 26, color: ORANGE },
  companyName: { fontFamily: "Helvetica-Bold", fontSize: 13, marginTop: 4 },
  subtitle: { fontSize: 9, color: GRAY, marginTop: 2 },
  logoBox: {
    width: 70,
    height: 70,
    alignItems: "center",
    justifyContent: "center",
  },
  logoImage: { width: 70, height: 70, objectFit: "contain" },
  divider: {
    borderBottomWidth: 2,
    borderBottomColor: ORANGE,
    marginTop: 16,
    marginBottom: 20,
  },
  infoRow: { flexDirection: "row", justifyContent: "space-between" },
  billedToTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 13,
    color: ORANGE,
    marginBottom: 6,
  },
  billedToLine: { flexDirection: "row", marginTop: 2 },
  billedToLabel: { width: 90, color: GRAY },
  billedToValue: { flex: 1 },
  metaBlock: { alignItems: "flex-end" },
  metaLine: { flexDirection: "row", marginTop: 4 },
  metaLabel: { color: GRAY, marginRight: 12 },
  metaValue: { fontFamily: "Helvetica-Bold", fontSize: 10 },
  metaValueOverdue: { color: "#c0392b" },
  table: { marginTop: 24, borderWidth: 1, borderColor: "#eee" },
  tableHeaderRow: { flexDirection: "row", backgroundColor: ORANGE_LIGHT },
  tableHeaderCell: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    padding: 8,
    textAlign: "center",
  },
  tableRow: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee" },
  tableCell: { fontSize: 9, padding: 8, textAlign: "center" },
  tableCellLeft: { textAlign: "left" },
  tableCellRight: { textAlign: "right" },
  subtotalRow: {
    flexDirection: "row",
    backgroundColor: ORANGE_LIGHT,
    borderTopWidth: 1,
    borderTopColor: ORANGE,
  },
  summaryBox: {
    marginTop: 14,
    alignSelf: "flex-end",
    width: "45%",
  },
  summaryLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 3,
  },
  summaryLabel: { color: GRAY },
  summaryValue: { fontFamily: "Helvetica-Bold" },
  remainingLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: ORANGE_LIGHT,
    padding: 6,
    marginTop: 4,
  },
  remainingLabel: { fontFamily: "Helvetica-Bold", fontSize: 10 },
  remainingValue: {
    fontFamily: "Helvetica-Bold",
    fontSize: 11,
    color: ORANGE,
  },
  sectionTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    marginTop: 20,
    marginBottom: 6,
  },
  paymentTable: { borderWidth: 1, borderColor: "#eee" },
  paymentHeaderRow: { flexDirection: "row", backgroundColor: "#f3f3f3" },
  paymentHeaderCell: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    padding: 6,
    textAlign: "center",
  },
  paymentRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: "#eee",
  },
  paymentCell: { fontSize: 8, padding: 6, textAlign: "center" },
  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 40,
  },
  footnote: { fontSize: 8, color: GRAY, maxWidth: "60%" },
  signatureBlock: { alignItems: "center" },
  signatureLine: { borderBottomWidth: 1, borderBottomColor: DARK, width: 160 },
  signatureName: { fontFamily: "Helvetica-Bold", fontSize: 10, marginTop: 4 },
  pageNumber: {
    position: "absolute",
    bottom: 20,
    right: 36,
    fontSize: 7,
    color: GRAY,
  },
  generatedAt: {
    position: "absolute",
    bottom: 20,
    left: 36,
    fontSize: 7,
    color: GRAY,
  },
});

export function ReceivablePdfDocument({
  receivable,
}: {
  receivable: Receivable;
}) {
  const generatedAt = formatGeneratedAt();
  const isOverdue = receivable.status === "overdue";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <Text style={styles.docTitle}>TAGIHAN</Text>
            <Text style={styles.companyName}>{COMPANY_NAME}</Text>
            <Text style={styles.subtitle}>
              Rincian tagihan atas transaksi yang belum lunas
            </Text>
          </View>
          <View style={styles.logoBox}>
            <Image src={logo} style={styles.logoImage} />
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.infoRow}>
          <View style={{ width: "60%" }}>
            <Text style={styles.billedToTitle}>Ditagihkan Kepada</Text>
            <View style={styles.billedToLine}>
              <Text style={styles.billedToLabel}>Nama</Text>
              <Text style={styles.billedToValue}>
                : {receivable.customerName || "Pelanggan Umum"}
              </Text>
            </View>
            <View style={styles.billedToLine}>
              <Text style={styles.billedToLabel}>Alamat Penagih</Text>
              <Text style={styles.billedToValue}>: {COMPANY_ADDRESS}</Text>
            </View>
          </View>

          <View style={styles.metaBlock}>
            <View style={styles.metaLine}>
              <Text style={styles.metaLabel}>Tanggal Transaksi</Text>
              <Text style={styles.metaValue}>
                {formatInvoiceDate(receivable.date)}
              </Text>
            </View>
            <View style={styles.metaLine}>
              <Text style={styles.metaLabel}>No. Transaksi</Text>
              <Text style={styles.metaValue}>
                {receivable.transactionCode ?? "-"}
              </Text>
            </View>
            <View style={styles.metaLine}>
              <Text style={styles.metaLabel}>Jatuh Tempo</Text>
              <Text
                style={[
                  styles.metaValue,
                  isOverdue ? styles.metaValueOverdue : undefined,
                ]}
              >
                {formatInvoiceDate(receivable.dueDate)}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeaderRow}>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: "40%", textAlign: "left" },
              ]}
            >
              Produk
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "15%" }]}>Qty</Text>
            <Text style={[styles.tableHeaderCell, { width: "20%" }]}>
              Harga Satuan
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "25%" }]}>
              Subtotal
            </Text>
          </View>

          {receivable.items.map((item, i) => (
            <View style={styles.tableRow} key={`${item.productName}-${i}`}>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellLeft,
                  { width: "40%" },
                ]}
              >
                {item.productName}
              </Text>
              <Text style={[styles.tableCell, { width: "15%" }]}>
                {item.qty}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellRight,
                  { width: "20%" },
                ]}
              >
                {formatRp(item.sellingPrice)}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellRight,
                  { width: "25%" },
                ]}
              >
                {formatRp(item.qty * item.sellingPrice)}
              </Text>
            </View>
          ))}

          <View style={styles.subtotalRow} wrap={false}>
            <Text style={[styles.tableCell, { width: "75%" }]} />
            <Text
              style={[
                styles.tableCell,
                styles.tableCellRight,
                { width: "25%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              {formatRp(receivable.totalAmount)}
            </Text>
          </View>
        </View>

        <View style={styles.summaryBox}>
          <View style={styles.summaryLine}>
            <Text style={styles.summaryLabel}>Total Tagihan</Text>
            <Text style={styles.summaryValue}>
              {formatRp(receivable.totalAmount)}
            </Text>
          </View>
          <View style={styles.summaryLine}>
            <Text style={styles.summaryLabel}>Sudah Dibayar</Text>
            <Text style={styles.summaryValue}>
              {formatRp(receivable.paidAmount)}
            </Text>
          </View>
          <View style={styles.remainingLine}>
            <Text style={styles.remainingLabel}>Sisa yang Harus Dibayar</Text>
            <Text style={styles.remainingValue}>
              {formatRp(receivable.remainingAmount)}
            </Text>
          </View>
        </View>

        {receivable.payments.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Riwayat Pembayaran</Text>
            <View style={styles.paymentTable}>
              <View style={styles.paymentHeaderRow}>
                <Text style={[styles.paymentHeaderCell, { width: "25%" }]}>
                  Tanggal
                </Text>
                <Text style={[styles.paymentHeaderCell, { width: "25%" }]}>
                  Metode
                </Text>
                <Text style={[styles.paymentHeaderCell, { width: "25%" }]}>
                  Jumlah
                </Text>
                <Text style={[styles.paymentHeaderCell, { width: "25%" }]}>
                  Status
                </Text>
              </View>
              {receivable.payments.map((p) => (
                <View style={styles.paymentRow} key={p.id}>
                  <Text style={[styles.paymentCell, { width: "25%" }]}>
                    {formatInvoiceDate(p.date)}
                    {p.code ? `\n${p.code}` : ""}
                  </Text>
                  <Text style={[styles.paymentCell, { width: "25%" }]}>
                    {PAYMENT_METHOD_LABELS[
                      p.method as keyof typeof PAYMENT_METHOD_LABELS
                    ] ?? p.method}
                  </Text>
                  <Text style={[styles.paymentCell, { width: "25%" }]}>
                    {formatRp(p.amount)}
                  </Text>
                  <Text style={[styles.paymentCell, { width: "25%" }]}>
                    {p.verificationStatus === "verified"
                      ? "Terverifikasi"
                      : "Belum Diverifikasi"}
                  </Text>
                </View>
              ))}
            </View>
          </>
        )}

        {receivable.notes && (
          <View style={{ marginTop: 14 }}>
            <Text style={{ fontSize: 8, color: GRAY, marginBottom: 2 }}>
              Keterangan
            </Text>
            <Text>{receivable.notes}</Text>
          </View>
        )}

        <View style={styles.footerRow}>
          <Text style={styles.footnote}>
            Mohon segera melakukan pelunasan sebelum tanggal jatuh tempo (
            {formatLongDate(receivable.dueDate)}). Tagihan ini dibuat
            berdasarkan transaksi tanggal {formatLongDate(receivable.date)}.
          </Text>
          <View style={styles.signatureBlock}>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{COMPANY_NAME}</Text>
          </View>
        </View>

        <Text style={styles.generatedAt} fixed>
          Dicetak pada {generatedAt}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Halaman ${pageNumber}/${totalPages} - Tagihan ${receivable.customerName || "Pelanggan Umum"}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}
